import React, { useEffect, useState } from "react";
import { X, Clock, Calendar } from "lucide-react";
import axiosInstance from "../../../utils/axiosInstance";
import API_BASE_URL from "../../../services/api";

const SlotModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  center, 
  slot, 
  mode = "create",
  title = "Manage Slot",
  categoryId
}) => {
  const [formData, setFormData] = useState({
    startTime: "",
    endTime: "",
    day: "all",
    capacity: "",
    isActive: true,
  });
  const [loading, setLoading] = useState(false);

  // Initialize form
  useEffect(() => {
    if (isOpen && slot && mode === "edit") {
      setFormData({
        startTime: slot.startTime || "",
        endTime: slot.endTime || "",
        day: slot.day || "all",
        capacity: slot.capacity || "",
        isActive: slot.isActive !== false,
      });
    } else if (isOpen && mode === "create") {
      setFormData({
        startTime: "",
        endTime: "",
        day: "all",
        capacity: "",
        isActive: true,
      });
    }
  }, [isOpen, slot, mode]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.startTime || !formData.endTime) {
      alert("Start time and end time are required");
      return;
    }
    
    if (formData.startTime >= formData.endTime) {
      alert("End time must be after start time");
      return;
    }

    setLoading(true);
    try {
const payload = {
  categoryId: Number(categoryId),  // ✅ required
  startTime: formData.startTime,
  endTime: formData.endTime,
  day: formData.day === "all" ? null : formData.day,
  capacity: formData.capacity ? parseInt(formData.capacity) : null,
  isActive: formData.isActive,
};


      if (mode === "edit" && slot?.id) {
        await axiosInstance.put(`${API_BASE_URL}/api/centers/slot/${slot.id}`, payload);
        alert("Slot updated successfully");
      } else {
        await axiosInstance.post(`${API_BASE_URL}/api/centers/${center.id}/slots`, payload);
        alert("Slot created successfully");
      }
      
      onSave();
      onClose();
    } catch (error) {
      console.error(`${mode === "edit" ? "Update" : "Creation"} failed:`, error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-3 pt-3 pb-16 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-6 sm:align-middle sm:max-w-sm sm:w-full">
          <div className="bg-white px-4 pt-4 pb-3">
            {/* Header */}
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center">
                <Clock className="w-4 h-4 text-blue-600 mr-1.5" />
                <h3 className="text-sm font-semibold text-gray-900">
                  {title}
                </h3>
              </div>
              <button 
                onClick={onClose} 
                className="text-gray-400 hover:text-gray-500 p-0.5 rounded hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Center Info */}
            {center && (
              <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-100 text-xs">
                <p className="font-medium text-blue-800">
                  Center: {center.name}
                </p>
                {center.city?.name && (
                  <p className="text-blue-600 mt-0.5">
                    Location: {center.city.name}
                  </p>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="space-y-3">
                {/* Time Range */}
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Start Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => handleChange("startTime", e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      End Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => handleChange("endTime", e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                {/* Day Selection */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <Calendar className="w-3 h-3 inline mr-1" />
                    Day Schedule
                  </label>
                  <select
                    value={formData.day}
                    onChange={(e) => handleChange("day", e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Days</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Select "All Days" for daily availability
                  </p>
                </div>

                {/* Capacity */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Maximum Capacity
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.capacity}
                    onChange={(e) => handleChange("capacity", e.target.value)}
                    placeholder="Leave empty for unlimited bookings"
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-0.5">
                    Set maximum number of bookings per slot
                  </p>
                </div>

                {/* Active Status */}
                <div className="flex items-center p-2 bg-gray-50 rounded border">
                  <input
                    id="isActive"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleChange("isActive", e.target.checked)}
                    className="h-3 w-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="isActive" className="ml-2 text-xs text-gray-700">
                    Active Slot (available for bookings)
                  </label>
                </div>

                {/* Preview */}
                <div className="bg-gray-50 p-2 rounded border border-gray-200">
                  <p className="text-xs font-medium text-gray-700 mb-0.5">Slot Preview:</p>
                  <p className="text-xs text-gray-900">
                    {formData.startTime && formData.endTime 
                      ? `${formData.startTime} - ${formData.endTime}`
                      : 'Set times to preview'
                    }
                    {formData.day !== 'all' && ` on ${formData.day.charAt(0).toUpperCase() + formData.day.slice(1)}`}
                    {formData.capacity && ` (Max: ${formData.capacity})`}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 border border-transparent rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {loading ? (
                    <>
                      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-solid border-current border-r-transparent mr-1.5"></span>
                      {mode === "edit" ? "Updating..." : "Creating..."}
                    </>
                  ) : (
                    `${mode === "edit" ? "Update" : "Create"} Slot`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlotModal;